export type DatedCounts = { date: string } & Record<string, number | string>;

/** Data key recharts uses for a series' rolling-average line. */
export const averageKey = (series: string, window: number) =>
  `${series} ${window}d avg`;

const DAY_MS = 24 * 60 * 60 * 1000;
const parseDay = (date: string) => Date.parse(`${date}T00:00:00Z`);
const formatDay = (ms: number) => new Date(ms).toISOString().slice(0, 10);

/**
 * Trailing rolling averages over *calendar* days, keyed by date.
 *
 * The stats endpoint omits days with no revisions entirely, so averaging the
 * last N array elements averages N present days rather than N calendar days
 * and inflates every figure across a quiet stretch. Missing days are zero-
 * filled between the first and last date before any window is applied.
 *
 * Pass the full unfiltered history: a 90 day average is only correct inside a
 * 90 day view if the 89 days before the window are part of the input.
 *
 * The first days of history have less than a full window behind them; those
 * divide by the days available so far rather than by the window size.
 */
export function rollingAveragesByDate(
  rows: DatedCounts[],
  keys: string[],
  windows: number[]
): Record<string, Record<string, number>> {
  if (rows.length === 0) return {};

  const byDate = new Map(rows.map((row) => [row.date, row]));
  const dates = [...byDate.keys()].sort();
  const startMs = parseDay(dates[0]);
  const dayCount =
    Math.round((parseDay(dates[dates.length - 1]) - startMs) / DAY_MS) + 1;

  const dense = Array.from({ length: dayCount }, (_, i) => {
    const date = formatDay(startMs + i * DAY_MS);
    const row = byDate.get(date);
    return { date, values: keys.map((key) => Number(row?.[key]) || 0) };
  });

  const result: Record<string, Record<string, number>> = {};
  // ponytail: O(days x window) resum per point; prefix sums if the history
  // ever outgrows a few thousand days.
  dense.forEach((day, i) => {
    const averages: Record<string, number> = {};
    for (const window of windows) {
      const span = Math.min(window, i + 1);
      keys.forEach((key, k) => {
        let sum = 0;
        for (let j = i - span + 1; j <= i; j++) sum += dense[j].values[k];
        averages[averageKey(key, window)] = sum / span;
      });
    }
    result[day.date] = averages;
  });
  return result;
}
